//
//  I18n/Dom.js
//  Tulip Web Modules SDK
//
//  Internationalization via manipulation of the DOM
//  © 2017 Tulip.
//


// - External Dependencies

/// Uses the jQuery Internationalization Extensions plugin.
/// The export from this plugin is not invoked directly (except to initialize)
/// but importing it means that internationalization extensions are added to the jQuery prototype.
/// https://github.com/i18next/jquery-i18next#introduction
import jQueryI18nPlugin from 'jquery-i18next';


// - Class Definition

/// Represents the DOM within the context of Internationalization.
export default class Dom {

  // - Instance Fields

  /// The instance of the internationalization framework to which all actual work is delegated.
  i18nFramework;

  /// The 'parent' representation of the DOM.
  parentDOM;

  /// True when completely initialized.
  fullyInitialized = false;

  /// A closure used to defer initialization.
  /// A no-op when invokation would be inappropriate, therefore safe to invoke whenever.
  finalizeInitialization = () => { };

  // - Instance Lifecycle

  /// Constructs a `new` I18n.Dom instance.
  /// Paramters:
  /// + i18next:  A constructed but uninitialized instance of the internationalization framework
  ///             which underpins the workings of this class.
  /// + dom:  A constructed and (optionally) initialized instance of a DOM representation.
  constructor(i18next, dom) {
    this.i18nFramework = i18next;
    this.parentDOM = dom;
  }

  /// Initializes the receiver with the given configuration options.
  /// https://github.com/i18next/jquery-i18next#initialize-the-plugin
  async init(overrideOptions) {
    // - Configuration

    // (to be passed to the jQuery Internationalization Extensions)
    const options = {
      // https://github.com/i18next/jquery-i18next#initialize-the-plugin
      // Tulip 'default' configuration which takes precedence over the plugin defaults.
      selectorAttr: 'data-translate',
      optionsAttr: 'translate-vars',
      // Module / SDK override configuration which takes precednce over all Tulip & plugin defaults.
      ...overrideOptions,
    };

    // - Verifiable Localization

    // Unfortunately, this class doesn't have a single point at which it controls the
    // output of the translation function in the way `I18n.translate` does.
    // Since what we want is just to prepend the Verifiable Localization symbol to whatever the `t`
    // function returns… we can just 'swizzle' the implementation of the `i18next.t` function that
    // is actually used by `jquery-i18next`.  So as to only affect HTML translation, we first have
    // to clone the `i18next` instance from that which is —by now— fully initialized by `../I18n`.
    // Of course, if Verifiable Localization is not permitted or not enabled, do nothing.

    // tulipNativeBridge is actually defined globally on the iPad, but ESLint doesn't know that.
    // We check for it being defined so that this SDK doesn't fail on HQ
    // where tulipNativeBridige is NOT defined.
    // eslint-disable-next-line no-undef
    let verifiableL10n = {};
    // eslint-disable-next-line no-undef
    if (typeof tulipNativeBridge !== 'undefined') {
      // eslint-disable-next-line no-undef
      verifiableL10n = (tulipNativeBridge.context.verifiable_l10n || {}).html;
    } else {
      verifiableL10n = ({}).html;
    }

    if (typeof verifiableL10n === 'string') {
      // A clone for I18n.Dom, otherwise I18n & I18n.Dom share an instance of the framework.
      this.i18nFramework = this.i18nFramework.cloneInstance();
      // A copy of the original `t` *method*, saved as a local *function* (i.e. bound).
      const t = this.i18nFramework.t.bind(this.i18nFramework);
      // Replace the `t` method with a function which defers back to the original via the local fnc.
      this.i18nFramework.t = (...args) => verifiableL10n + t(...args);
    }

    // The final steps of initialization need to be deferred unil jQuery is itself initialized.
    this.finalizeInitialization = () => {
      if (!this.parentDOM.$) { return; } // bail to wait for jQuery

      // - jQuery I18n Extensions

      jQueryI18nPlugin.init(this.i18nFramework, this.parentDOM.$, options);
      // …store those options for later, since the plugin doesn't expose them for us.
      jQueryI18nPlugin.options = options;

      // - Cleanup

      this.fullyInitialized = true;
      // It is unclear from documentation whether jquery-i18next::init may be invoked repeatedly.
      this.finalizeInitialization = () => { };
    };

    // Go ahead and make the first attempt now, it's possible jQuery is ready.
    this.finalizeInitialization();
  }

  // - Public Instance Methods

  /// Forwards invokation to the 'parent' DOM.
  use($) {
    this.parentDOM.use($);
    // Re-attempt since jQuery is now ready.
    this.finalizeInitialization();
  }

  /// Translates DOM elements.
  /// Invoke this function on page load in order to localize suitably attributed HTML tags.
  /// + selector:  A jQuery selector which specifies the DOM elements to be localized;
  ///              optional, if undefined then all suitably attributed HTML tags will be localized.
  /// + translationArgs:  An object containing interpolation arguments or other context; optional.
  translate(specificSelector, translationArgs = {}) {
    if (!this.fullyInitialized) { return; } // bail if not completely initialized

    const $ = this.parentDOM.$;
    const { optionsAttr, selectorAttr } = jQueryI18nPlugin.options;

    // The selector for 'actually applicable' / 'suitably attributed' elements:
    // '[…]' is the 'has attribute' selector, and selectorAttr is the name of the attribute
    // configured to be the `data-…` attribute whose value is the translation key
    // and thus whose presence indicates translation is needed.
    const translationSelector = `[${selectorAttr}]`;

    // Translate only the specific elements matching the given selector if one is given,
    // (coerce the parameter to a boolean to test whether any arguments were given)
    // otherwise translate all applicable elements.
    const translateWithinGivenScope = !!specificSelector;

    // - Determine the DOM nodes to translate.

    let nodes;
    if (translateWithinGivenScope) {
      // … only the applicable elements within the scope defined by the given selector.

      // `.find` only includes children of the receiver, but we want to include the root of the
      // given scope if the translation selector applies to it too.
      const specifiedScope = $(specificSelector);
      nodes = specifiedScope.find(translationSelector); // translation selector matched in scope
      if (specifiedScope.is(translationSelector)) { // translation selector matched against root
        nodes.add(specifiedScope);
      }
    } else {
      // … all the applicable elements.

      nodes = $(translationSelector); // translation selector matched globally
    }

    // - Actually translate each node.

    nodes.each((index, item) => {
      const node = $(item);

      // Combine the given translation arguments with those parsed from the DOM.
      let translationArguments;
      try {
        translationArguments = {
          ...translationArgs, // given as JavaScript argument
          ...JSON.parse(node.data(optionsAttr)), // parsed from HTML data attribute
        };
      } catch (e) {
        // Ignore the arguments data attribute if parsing it fails.
        translationArguments = translationArgs;
      }

      // localize() is a function added via the jQuery Internationalization Extensions.
      node.localize(translationArguments);
    });
  }

}



// WEBPACK FOOTER //
// ./src/i18n/Dom/Dom.js